import { useQuery } from 'convex/react';
import { api } from '@catchup/convex/convex/_generated/api';
import { format } from 'date-fns';
import type { Id } from '@catchup/convex/convex/_generated/dataModel';

interface HeatmapVisualizationProps {
  eventId: Id<"events">;
}

// Story 5.2: Participant with priority for VIP/High badges
interface ParticipantDetail {
  id: string;
  displayName: string;
  avatarUrl?: string;
  priority?: 'vip' | 'high' | 'normal';
}

interface HeatmapSlot {
  startTime: number;
  endTime: number;
  score: number;
  availableParticipantIds: string[];
  availableParticipants?: ParticipantDetail[];
  quorumMet: boolean;
  isPreferredTime?: boolean; // Story 5.3: Whether this slot matches organizer's time preference
  preferredTimeLabel?: string | null; // Story 5.3: Label like "Morning", "Evening"
}

interface HeatmapData {
  slots: HeatmapSlot[];
  bestSlotIndex?: number | null;
  bestSlot?: HeatmapSlot | null;
  participantCount: number;
  responseCount: number;
  computedAt?: number;
}

export function HeatmapVisualization({ eventId }: HeatmapVisualizationProps) {
  // Story 5.2: Use getHeatmapWithParticipants to get priority data for VIP badges
  const heatmapData = useQuery(api.heatmap.getHeatmapWithParticipants, { eventId }) as HeatmapData | undefined;

  // Loading state
  if (heatmapData === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-pulse mb-2">
            <div className="h-4 bg-gray-200 rounded w-48 mx-auto"></div>
          </div>
          <p className="text-gray-600">Loading heatmap...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!heatmapData.slots || heatmapData.slots.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-gray-600">No availability data yet. Waiting for responses...</p>
        </div>
      </div>
    );
  }

  /**
   * Story 5.2: Get priority badge for participant
   * Note: Currently unused, reserved for future participant detail display
   *
   * TODO: Implement individual participant badges in heatmap slot UI
   */
  /* const getPriorityBadge = (priority?: string): { label: string; className: string } | null => {
    if (priority === 'vip') {
      return { label: 'VIP', className: 'bg-purple-100 text-purple-800' };
    }
    if (priority === 'high') {
      return { label: 'High Priority', className: 'bg-orange-100 text-orange-800' };
    }
    return null;
  }; */

  /**
   * Story 5.2: Calculate weighted score breakdown
   * VIP = 3x weight, High = 2x weight, Normal = 1x weight
   */
  const calculateWeightedBreakdown = (participants: ParticipantDetail[] | undefined): {
    vipCount: number;
    highCount: number;
    normalCount: number;
    weightedScore: number;
    bonusPoints: number;
  } => {
    if (!participants || participants.length === 0) {
      return { vipCount: 0, highCount: 0, normalCount: 0, weightedScore: 0, bonusPoints: 0 };
    }

    let vipCount = 0;
    let highCount = 0;
    let normalCount = 0;
    let totalWeight = 0;

    participants.forEach(p => {
      if (p.priority === 'vip') {
        vipCount++;
        totalWeight += 3;
      } else if (p.priority === 'high') {
        highCount++;
        totalWeight += 2;
      } else {
        normalCount++;
        totalWeight += 1;
      }
    });

    // Bonus points from priority weighting (extra weight beyond base count)
    const baseWeight = participants.length;
    const bonusPoints = totalWeight - baseWeight;

    return { vipCount, highCount, normalCount, weightedScore: totalWeight, bonusPoints };
  };

  /**
   * Get color classes based on score and quorum status
   * High (75-100%): green
   * Medium (40-74%): yellow
   * Low (0-39%): red
   * Non-quorum: grayed/disabled appearance
   */
  const getColorClasses = (score: number, quorumMet: boolean): string => {
    // Story 5.5: Non-quorum slots get insufficient/grayed/disabled styling
    if (!quorumMet) {
      return 'bg-gray-100 border-gray-400 text-gray-500 insufficient grayed disabled';
    }

    if (score >= 75) {
      return 'bg-green-100 border-green-500 text-green-900';
    } else if (score >= 40) {
      return 'bg-yellow-100 border-yellow-500 text-yellow-900';
    } else {
      return 'bg-red-100 border-red-500 text-red-900';
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        Availability Heatmap
      </h2>

      <div className="space-y-3">
        {heatmapData.slots.map((slot, index) => {
          // Handle both null and undefined - bestSlotIndex can be null from Convex (Convex strips undefined)
          const isBestMatch = heatmapData.bestSlotIndex !== null &&
                              heatmapData.bestSlotIndex !== undefined &&
                              index === heatmapData.bestSlotIndex;
          const colorClasses = getColorClasses(slot.score, slot.quorumMet);
          const availableCount = slot.availableParticipantIds.length;
          const totalCount = heatmapData.participantCount;
          const participantFraction = `${availableCount}/${totalCount}`;

          // Story 5.2: Calculate weighted breakdown for VIP/High Priority display
          const breakdown = calculateWeightedBreakdown(slot.availableParticipants);

          return (
            <div
              key={`${slot.startTime}-${slot.endTime}`}
              data-testid={`heatmap-slot-${slot.startTime}-${slot.endTime}`}
              className={`
                ${colorClasses}
                border-2 rounded-lg p-4 relative
                hover:shadow-md transition-shadow duration-200
              `}
            >
              {/* Best Match Badge */}
              {isBestMatch && (
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    ⭐ Best Match
                  </span>
                </div>
              )}

              {/* Story 5.3: Preferred Time Badge */}
              {slot.isPreferredTime && !isBestMatch && (
                <div className="absolute top-2 right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    🕐 Preferred time
                  </span>
                </div>
              )}
              {slot.isPreferredTime && isBestMatch && (
                <div className="absolute top-2 right-24">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    🕐 Preferred time
                  </span>
                </div>
              )}

              {/* Time Slot */}
              <div className="mb-2">
                <p className="font-semibold text-sm">
                  {format(new Date(slot.startTime), 'EEE, MMM d • h:mm a')}
                </p>
                <p className="text-xs opacity-75">
                  {format(new Date(slot.endTime), 'h:mm a')}
                </p>
              </div>

              {/* Story 5.2: VIP and High Priority Badges */}
              {(breakdown.vipCount > 0 || breakdown.highCount > 0) && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {breakdown.vipCount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      VIP ({breakdown.vipCount} available)
                    </span>
                  )}
                  {breakdown.highCount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                      High Priority ({breakdown.highCount} available)
                    </span>
                  )}
                </div>
              )}

              {/* Availability Stats */}
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center space-x-4">
                  <div>
                    <p className="text-2xl font-bold">{slot.score}%</p>
                    <p className="text-xs opacity-75">Score</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{participantFraction}</p>
                    <p className="text-xs opacity-75">Available</p>
                  </div>
                </div>

                {/* Quorum Indicator */}
                {slot.quorumMet ? (
                  <div className="flex items-center space-x-1 text-sm text-green-700">
                    <span>✓</span>
                    <span className="text-xs">Quorum met</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-sm text-gray-500">
                    <span>⚠</span>
                    <span className="text-xs">Insufficient</span>
                  </div>
                )}
              </div>

              {/* Story 5.2: Priority Impact / Weighted Score Breakdown */}
              {breakdown.bonusPoints > 0 && (
                <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                  <p className="text-xs opacity-75">
                    Priority impact: +{breakdown.bonusPoints} pts from VIP/High Priority members
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          {heatmapData.responseCount} of {heatmapData.participantCount} participants responded
        </p>
      </div>
    </div>
  );
}
